import { CircleAlert } from "lucide-react";
import { useEffect, useState } from "react";

import {
  fetchClientName,
  fetchSessionVerdict,
  parseConsentRequest,
  signInAgainPath,
  submitConsent,
} from "../auth/oauth-connector";
import { signOut } from "../auth/sign-out";
import { AuthLayout } from "./auth-layout";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@nt/ui/button";
import { Spinner } from "@/components/ui/spinner";

export type ConsentViewState =
  | { kind: "checking" }
  | { kind: "signed-out" }
  | { kind: "not-admin" }
  | { kind: "unavailable" }
  | {
      kind: "ready";
      clientId: string;
      clientName: string;
      scopes: string[];
      pending: "approve" | "deny" | null;
      error: string | null;
    };

/**
 * The host of a Client ID Metadata Document `client_id`: the auth Worker only
 * accepts one whose HTTPS URL served a matching document, so the host is the
 * verified publisher of the client.
 */
function clientPublisher(clientId: string): string | null {
  if (!clientId.startsWith("https://")) return null;
  try {
    return new URL(clientId).hostname;
  } catch {
    return null;
  }
}

/**
 * The consent screen, one state at a time. Pure of I/O so each state is
 * provable without a browser; `OAuthConsent` below drives it.
 *
 * The non-admin state is the one that has to be unmistakable. The sign-in form
 * lets ANY account in (the console's role gate is per request, not per
 * sign-in), so a customer who follows the connector link lands here signed in.
 * They get a plain refusal and a way to switch account — never an Approve
 * button, and never a silent loop back to sign-in.
 */
export function OAuthConsentView({
  state,
  onDecision,
  onSignInAgain,
}: {
  state: ConsentViewState;
  onDecision: (accept: boolean) => void;
  onSignInAgain: () => void;
}) {
  if (state.kind === "checking") {
    return (
      <AuthLayout title="Connect Operator">
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Checking your session…
        </p>
      </AuthLayout>
    );
  }

  if (state.kind === "signed-out") {
    return (
      <AuthLayout title="Sign in to continue">
        <div className="space-y-4">
          <Alert role="status">
            <AlertDescription>
              Your session has ended. Sign in with your admin account to finish
              connecting.
            </AlertDescription>
          </Alert>
          <Button size="lg" className="w-full" onClick={onSignInAgain}>
            Sign in
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === "not-admin") {
    return (
      <AuthLayout title="Connect Operator">
        <div className="space-y-4">
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>This account isn&apos;t an admin</AlertTitle>
            <AlertDescription>
              Sign in with your admin account to connect Operator. Nothing was
              approved.
            </AlertDescription>
          </Alert>
          <Button
            size="lg"
            variant="outline"
            className="w-full"
            onClick={onSignInAgain}
          >
            Sign in with a different account
          </Button>
        </div>
      </AuthLayout>
    );
  }

  if (state.kind === "unavailable") {
    return (
      <AuthLayout title="Connect Operator">
        <div className="space-y-4">
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>We could not check your session</AlertTitle>
            <AlertDescription>
              Reload this page to try again. If it keeps failing, return to the
              app you are connecting and start again.
            </AlertDescription>
          </Alert>
          <Button
            size="lg"
            variant="outline"
            className="w-full"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const publisher = clientPublisher(state.clientId);
  const name = state.clientName || publisher || "An app";
  return (
    <AuthLayout
      title="Connect Operator"
      subtitle={`${name} is asking to act as you in the admin console.`}
      footer="Your admin role is checked on every request."
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            Requesting client
          </p>
          <p className="text-base font-semibold text-foreground">{name}</p>
          {publisher ? (
            <p className="text-sm text-muted-foreground">
              Published by {publisher}
            </p>
          ) : null}
          <code className="block rounded-control bg-surface px-3 py-2 font-mono text-sm break-all text-foreground">
            {state.clientId}
          </code>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium text-foreground">
            Requested access
          </p>
          <div className="flex flex-wrap gap-2">
            {state.scopes.map((scope) => (
              <Badge key={scope} variant="secondary">
                {scope}
              </Badge>
            ))}
          </div>
          <p className="text-sm text-muted-foreground">
            Approving lets this client use the Operator connector as you: read
            customer and company records in the admin console and run the admin
            actions Operator allows. Your admin role is re-checked on every
            request, deletions still wait for approval here, and access ends
            when you disconnect it in the client or your admin role is removed.
            Deny to give it nothing.
          </p>
        </div>

        {state.error ? (
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertTitle>Authorization failed</AlertTitle>
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={state.pending !== null}
            onClick={() => onDecision(false)}
          >
            {state.pending === "deny" ? <Spinner /> : null}
            Deny
          </Button>
          <Button
            type="button"
            disabled={state.pending !== null}
            onClick={() => onDecision(true)}
          >
            {state.pending === "approve" ? <Spinner /> : null}
            Approve
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}

/**
 * `/oauth/consent` — where the auth Worker sends a signed-in visitor whose
 * authorization needs approval. Outside the console gate (`console-routes.tsx`):
 * a non-admin has no console to see, and the refusal is this page's job.
 */
export function OAuthConsent() {
  const [state, setState] = useState<ConsentViewState>({ kind: "checking" });

  useEffect(() => {
    let cancelled = false;
    const request = parseConsentRequest(window.location.search);
    void (async () => {
      const verdict = await fetchSessionVerdict();
      if (cancelled) return;
      if (verdict !== "admin") {
        setState({ kind: verdict });
        return;
      }
      const clientName = await fetchClientName(request.clientId);
      if (cancelled) return;
      setState({
        kind: "ready",
        clientId: request.clientId,
        clientName,
        scopes: request.scopes,
        pending: null,
        error: null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function decide(accept: boolean) {
    if (state.kind !== "ready" || state.pending !== null) return;
    setState({ ...state, error: null, pending: accept ? "approve" : "deny" });
    const outcome = await submitConsent({
      accept,
      oauthQuery: parseConsentRequest(window.location.search).oauthQuery,
    });
    if (outcome.ok) {
      // The authorization code (or the denial) rides the client's own
      // redirect URI: this is the hand-back to claude.ai.
      window.location.assign(outcome.redirectTo);
      return;
    }
    if (outcome.reason === "signed-out") {
      setState({ kind: "signed-out" });
      return;
    }
    setState({ ...state, pending: null, error: outcome.message });
  }

  return (
    <OAuthConsentView
      state={state}
      onDecision={(accept) => void decide(accept)}
      onSignInAgain={() => {
        // A non-admin's session must end before the next sign-in, or the
        // authorize step would find it again and land straight back here.
        const target = signInAgainPath(window.location.search);
        if (state.kind === "not-admin") {
          void signOut({ handOver: () => window.location.assign(target) });
        } else {
          window.location.assign(target);
        }
      }}
    />
  );
}
